import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { Subscription, forkJoin } from 'rxjs';

import { CbApiService } from 'src/app/shared/cbapi.service';
import { DriveData, GetDriveResponse, GetStaticDataResponse } from 'src/app/shared/model';
import { SpinnerService } from 'src/app/shared/spinner/spinner.service';
import { ToastService } from 'src/app/shared/toast/toast.service';

interface QuestionCategory {
  category: string;
  questions: Array<{ number: number; text: string }>;
}

@Component({
  selector: 'app-drive-summary',
  templateUrl: './drive-summary.component.html',
  styleUrls: ['./drive-summary.component.scss']
})
export class DriveSummaryComponent implements OnInit, OnDestroy {

  driveId: string = '';
  collegeName: string = '';

  getDrivesDataSub!: Subscription;

  driveSummary!: GetDriveResponse;
  
  driveLogos!: DriveData;
  collegeNames!: DriveData;
  
  barRaiserCategories: QuestionCategory[] = [];

  constructor(
    private cbApiService: CbApiService,
    private spinnerService: SpinnerService,
    private toastService: ToastService,
    private route: ActivatedRoute) {}

  ngOnInit(): void {
    this.spinnerService.showSpinner();
    this.getDriveId();
    this.getDrivesData();
  }

  getDriveId(): void {
    this.route.parent?.params.subscribe(params => {
      this.driveId = params['driveId'];
    });
  }

  getDrivesData(): void {
    this.getDrivesDataSub = forkJoin([
      this.cbApiService.getDrive(this.driveId),
      this.cbApiService.getStaticData(),
    ]).subscribe(
      ([getDrivesSummaryResponse, getStaticDataResponse]) => {
        this.handleGetDrivesDataResponse(getDrivesSummaryResponse, getStaticDataResponse);
      },
      (error) => {
        this.toastService.showMessage('Failed to fetch drive summary', 'error');
        this.spinnerService.hideSpinner();
      }
    );
  }

  handleGetDrivesDataResponse(getDrivesSummaryResponse: GetDriveResponse, getStaticDataResponse: GetStaticDataResponse): void {
    if (getDrivesSummaryResponse?.success && getStaticDataResponse?.success) {
      this.driveSummary = getDrivesSummaryResponse;

      if (!this.driveSummary.body.TOTAL_COUNT) {
        this.driveSummary.body.TOTAL_COUNT = "0"
      }
      if (!this.driveSummary.body.SELECTED_COUNT) {
        this.driveSummary.body.SELECTED_COUNT = "0"
      }
      if (!this.driveSummary.body.TOTAL_PANEL) {
        this.driveSummary.body.TOTAL_PANEL = "0"
      }
      this.collegeNames = getStaticDataResponse.body?.DRIVE_COLLEGE_NAMES as DriveData;
      this.collegeName =  this.collegeNames[this.driveSummary.body?.COLLEGE];
      this.driveLogos = getStaticDataResponse.body?.DRIVE_LOGOS as DriveData;
      
      // Parse bar raiser questions
      if (this.hasBarRaiserQuestions()) {
        this.barRaiserCategories = this.parseBarRaiserQuestions();
      }
    } else {
      this.toastService.showMessage('Failed to fetch drive summary', 'error');
    }
    this.spinnerService.hideSpinner();
  }

  getDriveLogo(collegeName: string): string {
    let noImageLink = '/assets/images/no-image.jpg';
    
    if (this.driveLogos) {
      return this.driveLogos[collegeName] ? this.driveLogos[collegeName] : noImageLink;
    }

    return noImageLink;
  }

  getStatusClass(status: string): string {
    const statusUpper = status?.toUpperCase() || '';
    switch (statusUpper) {
      case 'UPCOMING':
        return 'status-badge upcoming';
      case 'ONGOING':
        return 'status-badge ongoing';
      case 'COMPLETED':
        return 'status-badge completed';
      default:
        return 'status-badge';
    }
  }

  parseBarRaiserQuestions(): QuestionCategory[] {
    if (!this.driveSummary?.body?.BAR_RAISER_QUESTIONS) {
      return [];
    }

    const questionsText = this.driveSummary.body.BAR_RAISER_QUESTIONS;
    const lines = questionsText.split('\n');
    const categories: QuestionCategory[] = [];
    let currentCategory: QuestionCategory | null = null;

    for (const line of lines) {
      const trimmedLine = line.trim();
      
      // Skip empty lines
      if (!trimmedLine) {
        continue;
      }

      // Treat markdown headers (e.g. "### Problem Solving (10)") as category headers
      if (trimmedLine.startsWith('###') || trimmedLine.startsWith('##') || trimmedLine.startsWith('#')) {
        const headerText = trimmedLine.replace(/^#+\s*/, '').trim();
        if (headerText) {
          if (currentCategory && currentCategory.questions.length > 0) {
            categories.push(currentCategory);
          }
          currentCategory = { category: headerText, questions: [] };
        }
        continue;
      }

      // Check if line is a category (text followed by colon, not starting with number)
      if (trimmedLine.includes(':') && !trimmedLine.match(/^\d+\./)) {
        // Save previous category if exists
        if (currentCategory && currentCategory.questions.length > 0) {
          categories.push(currentCategory);
        }
        
        // Start new category
        const categoryName = trimmedLine.replace(':', '').trim();
        currentCategory = {
          category: categoryName,
          questions: []
        };
      } 
      // Check if line is a numbered question
      else if (trimmedLine.match(/^\d+\./)) {
        if (!currentCategory) {
          currentCategory = { category: 'Questions', questions: [] };
        }

        const questionMatch = trimmedLine.match(/^(\d+)\.\s*(.+)$/);
        if (questionMatch) {
          const questionNumber = parseInt(questionMatch[1], 10);
          const questionText = questionMatch[2].trim();
          currentCategory.questions.push({
            number: questionNumber,
            text: questionText
          });
        }
      }
    }

    // Add last category if exists
    if (currentCategory && currentCategory.questions.length > 0) {
      categories.push(currentCategory);
    }

    return categories;
  }

  hasBarRaiserQuestions(): boolean {
    return !!(this.driveSummary?.body?.BAR_RAISER_QUESTIONS && 
              this.driveSummary.body.BAR_RAISER_QUESTIONS.trim().length > 0);
  }
  
  getBarRaiserTitle(): string {
    return 'Bar Raiser Questions';
  }

  isAiEnabled(): boolean {
    return this.driveSummary?.body?.AI_ENABLE?.toUpperCase() === 'TRUE';
  }

  getSelectionRate(): string {
    const total = parseInt(this.driveSummary?.body?.TOTAL_COUNT || '0');
    const selected = parseInt(this.driveSummary?.body?.SELECTED_COUNT || '0');
    
    if (total === 0) {
      return '0.0';
    }
    
    return ((selected / total) * 100).toFixed(1);
  }

  hasSelectionData(): boolean {
    const total = parseInt(this.driveSummary?.body?.TOTAL_COUNT || '0');
    return total > 0;
  }

  ngOnDestroy(): void {
    this.getDrivesDataSub?.unsubscribe();
  }
}
